import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, MenuItem, Paper, TextField } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { Callout, FieldWrapper, SectionHeader, StepActions, PageHeader } from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setAddressData, setShareHolderData } from "@store/slices/registerSlice";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { CODIGO_POSTAL } from "@utils/expreciones";
import { useLazyGetByCpQuery } from "@api/postalCodes";
import { usePatchAddressMutation, usePostAddressMutation } from "@api/address";
import { useLazyGetAddressTypeQuery } from "@api/catalogs";
import { usePostValidateNextStageMutation } from "@api/applications";
import { Address } from "@interfaces/catalogs";
import { ApiError } from "@interfaces/response";
import { ADDRESS_CODES, STATUS_CATALOG } from "@helpers/constantes";
import useStatusConfig from "@hooks/useStatusConfig";

interface Colonia {
  id: number;
  name: string;
}

export const DomicilioAccionista = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { nextStatus, previousStatus } = useStatusConfig(STATUS_CATALOG.domicilio_accionista.code);

  const clientDetails = useSelector((state: RootState) => state.register.personal_data);
  const addressData = useSelector((state: RootState) => state.register.address_data);
  const shareholder = useSelector((state: RootState) => state.register.shareholder);
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [getCp] = useLazyGetByCpQuery();
  const [getAddressTypes] = useLazyGetAddressTypeQuery();
  const [postAddress] = usePostAddressMutation();
  const [patchAddress] = usePatchAddressMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const [listColonias, setListColonias] = useState<Colonia[]>([]);
  const [addresType, setAddresType] = useState("");
  const [cpError, setCpError] = useState("");
  const [isFetchingCp, setIsFetchingCp] = useState(false);

  const formik = useFormik({
    initialValues: {
      cp: "",
      calle: "",
      num_ext: "",
      num_int: "",
      state_id: "",
      city_id: "",
      colony_id: "",
      state_name: "",
      city_name: "",
      locality_id: "",
      locality_name: "",
      otro: "",
    },
    validationSchema: yup.object().shape({
      cp: yup.string().required("Campo requerido").matches(CODIGO_POSTAL, "Código Postal inválido"),
      calle: yup.string().required("Campo requerido"),
      num_ext: yup.string().required("Campo requerido"),
      state_name: yup.string().required("Campo requerido"),
      city_name: yup.string(),
      locality_id: yup.string().required("Campo requerido"),
      locality_name: yup.string().required("Campo requerido"),
      colony_id: yup.string().required("Campo requerido"),
      otro: yup.string().when("colony_id", {
        is: "-1",
        then: (schema) => schema.required("Campo requerido"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) return;

      try {
        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }

        setSubmitting(true);
        const dataAddress = {
          street: values.calle,
          no_ext: values.num_ext,
          no_int: values.num_int,
          colonia_id: Number(values.colony_id) === -1 ? null : +values.colony_id,
          state_id: values.state_id ? +values.state_id : null,
          city_id: values.city_id ? +values.city_id : null,
          locality_id: values.locality_id ? +values.locality_id : null,
          country_id: 1,
          cp: values.cp,
          client_details_id: clientDetails.id,
          person_id: shareholder.id,
          address_type_id: addresType,
          other_neighborhood: values.otro,
        };

        let resultAddress = null;

        if (addressData && addressData.id) {
          resultAddress = await patchAddress({ addressId: addressData.id, data: dataAddress }).unwrap();
        } else {
          resultAddress = await postAddress(dataAddress).unwrap();
        }

        dispatch(
          setAddressData({
            id: resultAddress.data.id,
            street: values.calle,
            no_ext: values.num_ext,
            no_int: values.num_int,
            colony_id: +values.colony_id,
            state_id: +values.state_id,
            city_id: +values.city_id,
            locality_id: +values.locality_id,
            postal_code: values.cp,
            state_name: values.state_name,
            city_name: values.city_name,
            locality_name: values.locality_name,
            other_neighborhood: values.otro,
          }),
        );

        dispatch(
          setShareHolderData({
            ...shareholder,
            address_verification_required: false,
          }),
        );

        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: nextStatus.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        navigate(nextStatus?.url || "");
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, { extra: { component: "AccionistaDireccion", values: formik.values } });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleCp = async (value: string) => {
    try {
      setIsFetchingCp(true);
      const cpData = await getCp(value);
      if (cpData) {
        const selecteState = cpData.data?.states[0];
        const selectedCity = cpData.data?.cities[0];
        const selectedLocality = cpData.data?.localities[0];
        const selectedColonia = cpData.data?.colonias[0];

        if (!selecteState || !selectedColonia || !selectedLocality) {
          formik.setFieldValue("state_id", "");
          formik.setFieldValue("state_name", "");
          formik.setFieldValue("city_id", "");
          formik.setFieldValue("city_name", "");
          formik.setFieldValue("locality_id", "");
          formik.setFieldValue("locality_name", "");
          formik.setFieldValue("colony_id", "");
          setCpError("No existe el código postal");
          setListColonias([]);
          return;
        }

        setCpError("");
        formik.setFieldValue("state_id", selecteState ? selecteState.id : "");
        formik.setFieldValue("state_name", selecteState ? selecteState.name : "");
        formik.setFieldValue("city_id", selectedCity ? selectedCity.id : "");
        formik.setFieldValue("city_name", selectedCity ? selectedCity.name : "");
        formik.setFieldValue("locality_id", selectedLocality ? selectedLocality.id : "");
        formik.setFieldValue("locality_name", selectedLocality ? selectedLocality.name : "");
        formik.setFieldValue("colony_id", selectedColonia ? selectedColonia.id : "");
        setListColonias(cpData.data?.colonias);
      }
    } catch (error) {
      console.error("Ha ocurrido un error al consultar el código postal", error);
    } finally {
      setIsFetchingCp(false);
    }
  };

  // Tipo de domicilio particular (`CDPA`): se consulta al entrar; sin él no se puede guardar.
  useEffect(() => {
    getAddressTypes()
      .unwrap()
      .then((types) =>
        setAddresType(types.find((address: Address) => address.code === ADDRESS_CODES.particular.code)?.id ?? ""),
      )
      .catch((error) => {
        Sentry.captureException(error, { extra: { component: "DomicilioAccionista-address_types" } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      });
  }, [getAddressTypes, dispatch]);

  useEffect(() => {
    if (addressData && addressData.id) {
      handleCp(addressData.postal_code);
      formik.setFieldValue("cp", addressData.postal_code);
      formik.setFieldValue("calle", addressData.street);
      formik.setFieldValue("num_ext", addressData.no_ext);
      formik.setFieldValue("num_int", addressData.no_int);
      setTimeout(() => {
        formik.setFieldValue("colony_id", addressData.colony_id);
        formik.setFieldValue("otro", addressData.other_neighborhood);
      }, 800);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressData]);

  return (
    <Box>
      <PageHeader
        eyebrow="Accionista mayoritario"
        title="Domicilio del accionista"
        subtitle="Ingresa el domicilio particular del accionista mayoritario."
      />

      {shareholder.address_verification_required && (
        <Box sx={{ mb: 2 }}>
          <Callout tone="warn">
            Se ha modificado el RFC o CURP, por favor verifica el domicilio antes de continuar.
          </Callout>
        </Box>
      )}

      <Paper variant="lendia_paper" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SectionHeader label="Dirección" first />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
          <FieldWrapper
            label="Código Postal"
            loading={isFetchingCp}
            error={cpError || (formik.touched.cp && formik.errors.cp) || ""}
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="cp"
              value={formik.values.cp}
              onBlur={formik.handleBlur}
              onChange={(e) => {
                const cp = e.target.value.replace(/[^0-9]/g, "");
                formik.setFieldValue("cp", cp);
                if (cp.length === 5) {
                  handleCp(cp);
                }
              }}
              placeholder="00000"
              inputProps={{ maxLength: 5, "data-testid": "accionista-direccion-cp-input" }}
              disabled={formik.isSubmitting}
            />
          </FieldWrapper>

          <FieldWrapper label="Calle o avenida" full error={(formik.touched.calle && formik.errors.calle) || ""}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="calle"
              value={formik.values.calle}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Calle o avenida"
              inputProps={{ maxLength: 100, "data-testid": "accionista-direccion-calle-input" }}
              disabled={formik.isSubmitting}
            />
          </FieldWrapper>

          <FieldWrapper label="No. Exterior" error={(formik.touched.num_ext && formik.errors.num_ext) || ""}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="num_ext"
              value={formik.values.num_ext}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Ej. 42"
              inputProps={{ maxLength: 50, "data-testid": "accionista-direccion-num-ext-input" }}
              disabled={formik.isSubmitting}
            />
          </FieldWrapper>
          <FieldWrapper label="No. Interior" optionalSuffix="(opcional)">
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="num_int"
              value={formik.values.num_int}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Ej. 3B"
              inputProps={{ maxLength: 50, "data-testid": "accionista-direccion-num-int-input" }}
              disabled={formik.isSubmitting}
            />
          </FieldWrapper>

          <FieldWrapper label="Colonia" full error={(formik.touched.colony_id && formik.errors.colony_id) || ""}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              select
              fullWidth
              name="colony_id"
              value={formik.values.colony_id}
              onBlur={formik.handleBlur}
              onChange={(e) => {
                formik.handleChange(e);
                if (+e.target.value === -1) {
                  formik.setFieldValue("otro", "");
                }
              }}
              inputProps={{ "data-testid": "accionista-direccion-colonia-select" }}
              disabled={formik.isSubmitting}
            >
              <MenuItem value="">Selecciona…</MenuItem>
              {listColonias.map((colonia) => (
                <MenuItem key={colonia.id} value={colonia.id}>
                  {colonia.name}
                </MenuItem>
              ))}
            </TextField>
          </FieldWrapper>

          {String(formik.values.colony_id) === "-1" && (
            <FieldWrapper label="Otra colonia" full error={(formik.touched.otro && formik.errors.otro) || ""}>
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                name="otro"
                value={formik.values.otro}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                inputProps={{ maxLength: 100, "data-testid": "accionista-direccion-otro-colonia-input" }}
                disabled={formik.isSubmitting}
              />
            </FieldWrapper>
          )}

          <FieldWrapper label="Ciudad">
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="city_name"
              value={formik.values.city_name}
              disabled
              inputProps={{ "data-testid": "accionista-direccion-ciudad-input" }}
            />
          </FieldWrapper>
          <FieldWrapper label="Municipio">
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="locality_name"
              value={formik.values.locality_name}
              disabled
              inputProps={{ "data-testid": "accionista-direccion-municipio-input" }}
            />
          </FieldWrapper>
          <FieldWrapper label="Entidad federativa" full>
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="state_name"
              value={formik.values.state_name}
              disabled
              inputProps={{ "data-testid": "accionista-direccion-estado-input" }}
            />
          </FieldWrapper>
        </Box>
      </Paper>

      <StepActions
        onBack={() => {
          if (previousStatus) navigate(previousStatus.url);
        }}
        onNext={formik.submitForm}
        nextDisabled={!formik.dirty || !formik.isValid || formik.isSubmitting}
        backTestId="accionista-direccion-back-button"
        nextTestId="accionista-direccion-continue-button"
      />
    </Box>
  );
};
